<script setup>
// This starter template is using Vue 3 <script setup> SFCs
// Check out https://vuejs.org/api/sfc-script-setup.html#script-setup
</script>

<template>
  <div id="app-shell">
    <header class="arka-topbar" v-if="shouldShowHeader">
      <img :src="arkaLogo" alt="ARKA" class="arka-topbar__logo" />
    </header>
    <main class="arka-main">
      <router-view />
    </main>
  </div>
</template>

<script>
import API from '@/services/api/';
import { setThemeColors } from './pages/utils/themeUtility';
import appMode from './services/appMode';
import { setupConsoleForward } from './utils/rnBridge';
import arkaLogo from '@/assets/Logo.png';

  export default {
    name: 'App',
    data() {
      return { arkaLogo };
    },
    computed: {
      shouldShowHeader() {
        const route = this.$route;
        const url = window.location.hostname;
        const initialName = url.split(".")[0];

        // Always show header in app mode (loaded from the app with ?arkaApp param)
        if (appMode.isAppMode()) {
          return true;
        }

        // Blacklist: Explicitly hide header for these routes regardless of hostname
        // These are separate tools that should not show the marketing portal header
        const excludedRoutes = ['solarCalculator', 'waaree', 'test'];
        if (excludedRoutes.includes(route.name)) {
          return false;
        }

        const marketingPortalHostnames = [
          'solark',
          'marketing',
          'arka'
        ];

        const isMarketingHostname = marketingPortalHostnames.some(hostname =>
          initialName.toLowerCase().includes(hostname)
        );

        if (!isMarketingHostname) {
          return false;
        }

        const marketingPortalRoutePatterns = [
          'homePage', 'siteSurvey', 'filesUploadSuccessfully',
          'mapForDesignTool', 'reportSummary',
          'solarkMarketingPortal', 'solarkReport',
          'siteSurveyPreview', 'formSubmission', 'editNotAllowed',
          'proposalTemplate', 'systemConfigRouteFile'
        ];

        const routePrefixes = [
          'homeMap', 'uploadPhotos', 'uploadVideos', 'allRoofs',
          'studio'
        ];

        if (marketingPortalRoutePatterns.includes(route.name)) {
          return true;
        }

        const matchesPrefix = routePrefixes.some(prefix =>
          route.name && route.name.startsWith(prefix)
        );

        return matchesPrefix || !route.name;
      }
    },
    async created(){
      API.SET_AXIOS_RESPONSE_HANDLER();
      API.SET_DATABASE_URL();
      // SAS token is for Azure Blob document access — not needed in app/studio mode.
      // Skipping it prevents a CORS error when the mobile WebView origin is a LAN IP.
      appMode.initialize();
      if (appMode.isAppMode()) {
        setupConsoleForward();
      } else {
        API.UPDATE_SAS_TOKEN();
      }
      setThemeColors();
    }
  };
</script>

<style>
* {
  padding: 0;
  margin: 0;
  word-break: break-word;
  box-sizing: border-box;
  font-family: "Helvetica Neue";
}

body {
  font-family: "Helvetica Neue";
}

#app-shell {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}

.arka-topbar {
  display: flex;
  align-items: center;
  height: 56px;
  padding: 0 24px;
  background: linear-gradient(135deg, #4DA6F5 0%, #3B8FE8 100%);
  flex-shrink: 0;
  z-index: 1000;
  position: relative;
}

.arka-topbar__logo {
  height: 28px;
  width: auto;
  filter: brightness(0) invert(1);
}

.arka-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

@media (max-width: 500px) {
  .arka-topbar {
    height: 75px;
    padding: 0 16px;
  }

  .arka-topbar__logo {
    height: 22px;
  }
}

@font-face {
  font-family: "Helvetica Neue";
  src: url(/assets/HelveticaNeue.otf);
}

@font-face {
  font-family: "Switzer";
  src: url("./pages/studio/proposalTemplateReportComponents/assets/fonts/Switzer-Regular.otf");
  font-weight: 400;
}

@font-face {
  font-family: "Switzer";
  src: url("./pages/studio/proposalTemplateReportComponents/assets/fonts/Switzer-Medium.otf");
  font-weight: 500;
}

@font-face {
  font-family: "Switzer";
  src: url("./pages/studio/proposalTemplateReportComponents/assets/fonts/Switzer-Semibold.otf");
  font-weight: 600;
}

</style>
